import React, { useEffect, useState } from "react";
import { useRootSelector } from "~redux/hooks";
import {
  EquipmentTemplateSelectOption,
  type EquipmentTemplateBodyProps,
} from "components/views/template/EquipmentTemplateMenu";
import { aselFlightplanSelector } from "~redux/slices/aircraftSlice";
import type { DatalinkCode, ACARSCode, SatCommCode, VoiceCommCode } from "@poscon/shared-types";
import {
  isACARSCode,
  isDatalinkCode,
  isSatCommCode,
  isVoiceCommCode,
  unsafeEntries,
} from "@poscon/shared-types";
import { colorNameMap, getBitmapTextStyles, Label, useInputProps } from "@poscon/shared-frontend";
import { edstFontName, edstFontDim } from "~/utils/constants";
import { EdstInput } from "~/components/utils/EdstInput";

const tint = colorNameMap.grey;

const voiceCatText = {
  H: "(HF)",
  V: "(VHF)",
  U: "(UHF)",
  Y: "(8.33 kHZ)",
};

const cpdlcCatText = {
  J1: "(ATN VDL MODE 2)",
  J2: "(FANS1/A HF)",
  J3: "(FANS1/A VDL MODE A)",
  J4: "(FANS1/A VDL MODE 2)",
  J5: "(FANS1/A INMARSAT)",
  J6: "(FANS1/A MTSAT)",
  J7: "(FANS1/A IRIDIUM)",
};

const acarsCatText = {
  E1: "(FMCWPR)",
  E2: "(D-FIS)",
  E3: "(PDC)",
};

const satCatText = {
  M1: "(INMARSAT)",
  M2: "(MTSAT)",
  M3: "(IRIDIUM)",
};

export const EquipmentCommTemplate = ({ setReset, fp }: EquipmentTemplateBodyProps) => {
  const [voiceCategories, setVoiceCategories] = useState<VoiceCommCode[]>([]);
  const [cpdlcCategories, setCpdlcCategories] = useState<DatalinkCode[]>([]);
  const [acarsCategories, setAcarsCategories] = useState<ACARSCode[]>([]);
  const [satelliteCategories, setSatelliteCategories] = useState<SatCommCode[]>([]);

  const datInputProps = useInputProps(
    `eqp/comm/dat`,
    "",
    (s) => {
      // TODO: implement
    },
    {
      maxLength: 50,
      focusOnMount: false,
    }
  );

  const comInputProps = useInputProps(
    `eqp/comm/com`,
    "",
    (s) => {
      // TODO: implement
    },
    {
      maxLength: 50,
      focusOnMount: false,
    }
  );

  const toggleCategory = (cat: VoiceCommCode | DatalinkCode | ACARSCode | SatCommCode) => {
    if (isDatalinkCode(cat)) {
      const cpdlcCats = [...cpdlcCategories];
      const index = cpdlcCats.indexOf(cat);
      if (index < 0) {
        setCpdlcCategories([...cpdlcCats, cat]);
      } else {
        cpdlcCats.splice(index, 1);
        setCpdlcCategories(cpdlcCats);
      }
    } else if (isACARSCode(cat)) {
      const acarsCats = [...acarsCategories];
      const index = acarsCats.indexOf(cat);
      if (index < 0) {
        setAcarsCategories([...acarsCats, cat]);
      } else {
        acarsCats.splice(index, 1);
        setAcarsCategories(acarsCats);
      }
    } else if (isSatCommCode(cat)) {
      const satCats = [...satelliteCategories];
      const index = satCats.indexOf(cat);
      if (index < 0) {
        setSatelliteCategories([...satCats, cat]);
      } else {
        satCats.splice(index, 1);
        setSatelliteCategories(satCats);
      }
    } else if (isVoiceCommCode(cat)) {
      const voiceCats = [...voiceCategories];
      const index = voiceCats.indexOf(cat);
      if (index < 0) {
        setVoiceCategories([...voiceCats, cat]);
      } else {
        voiceCats.splice(index, 1);
        setVoiceCategories(voiceCats);
      }
    }
  };

  useEffect(() => {
    const field10a = fp?.communicationCodes ?? [];
    const voiceCats = field10a.filter(isVoiceCommCode);
    const acarsCats = field10a.filter(isACARSCode);
    const satCats = field10a.filter(isSatCommCode);
    const cpdlcCats = field10a.filter(isDatalinkCode);

    const reset = () => {
      setVoiceCategories(voiceCats);
      setCpdlcCategories(cpdlcCats);
      setAcarsCategories(acarsCats);
      setSatelliteCategories(satCats);
    };

    setReset(reset);
    reset();
  }, [fp?.communicationCodes, setReset]);

  return (
    <>
      <container x={20} y={edstFontDim.height * 12}>
        <bitmapText text="DAT/" y={4} style={{ ...getBitmapTextStyles(edstFontName), fill: tint }} />
        <EdstInput
          {...datInputProps}
          x={Math.floor(edstFontDim.width * 4.5)}
          width={edstFontDim.width * 50 + 4}
        />
        <bitmapText
          text="COM/"
          y={edstFontDim.height * 2 + 2}
          style={{ ...getBitmapTextStyles(edstFontName), fill: tint }}
        />
        <EdstInput
          {...comInputProps}
          x={Math.floor(edstFontDim.width * 4.5)}
          y={edstFontDim.height * 2 - 2}
          width={edstFontDim.width * 50 + 4}
        />
      </container>
      <container x={30}>
        <Label text="VOICE CATEGORY" />
        <container y={edstFontDim.height + 8}>
          {unsafeEntries(voiceCatText).map(([k, v], i) => (
            <EquipmentTemplateSelectOption
              key={k}
              y={(edstFontDim.height + 3) * i}
              text={`${k} ${v}`}
              variant="square"
              onmousedown={() => toggleCategory(k)}
              selected={voiceCategories.includes(k)}
            />
          ))}
        </container>
      </container>
      <container x={edstFontDim.width * 25}>
        <Label text="CPDLC CATEGORY" />
        <container y={edstFontDim.height + 8}>
          {unsafeEntries(cpdlcCatText).map(([k, v], i) => (
            <EquipmentTemplateSelectOption
              key={k}
              y={(edstFontDim.height + 3) * i}
              text={`${k} ${v}`}
              variant="square"
              onmousedown={() => toggleCategory(k)}
              selected={cpdlcCategories.includes(k)}
            />
          ))}
        </container>
      </container>
      <container x={edstFontDim.width * 60}>
        <Label text="ACARS CATEGORY" />
        <container y={edstFontDim.height + 8}>
          {unsafeEntries(acarsCatText).map(([k, v], i) => (
            <EquipmentTemplateSelectOption
              key={k}
              y={(edstFontDim.height + 3) * i}
              text={`${k} ${v}`}
              variant="square"
              onmousedown={() => toggleCategory(k)}
              selected={acarsCategories.includes(k)}
            />
          ))}
        </container>
      </container>
      <container x={edstFontDim.width * 80}>
        <Label text="SATELLITE RTF" />
        <container y={edstFontDim.height + 8}>
          {unsafeEntries(satCatText).map(([k, v], i) => (
            <EquipmentTemplateSelectOption
              key={k}
              y={(edstFontDim.height + 3) * i}
              text={`${k} ${v}`}
              variant="square"
              onmousedown={() => toggleCategory(k)}
              selected={satelliteCategories.includes(k)}
            />
          ))}
        </container>
      </container>
    </>
  );
};
